import React, {Component, Fragment, Suspense} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import * as Sentry from '@sentry/react';
import {RichTextView} from './RichTextView';

// Una descarga que falla se reintenta una vez antes de rendirse: un corte de red o un deploy en
// el medio no tienen por qué dejar el bloque en sólo lectura.
const RETRY_IMPORT_MS = 300;
export function loadEditorModule(load = () => import('./RichTextEditor')) {
    return load().catch(() => new Promise((resolve) => setTimeout(resolve, RETRY_IMPORT_MS)).then(load));
}

// Diferido: TipTap (~100 KB) sólo baja cuando alguien edita un bloque de texto. El portal y la
// vista previa dibujan con `RichTextView`, que no lo importa.
const RichTextEditor = React.lazy(() => loadEditorModule());

// Un solo aviso aunque haya varios bloques: un `toast` con un id ya activo se descarta.
const LOAD_ERROR_TOAST_ID = 'informes-text-editor-load-error';

/**
 * Si el editor no carga o revienta, el error no puede subir al `ErrorBoundary` de la app: la
 * agencia vería «Algo salió mal» a pantalla completa y perdería el informe sin guardar. Mismo patrón
 * que `Ga4AttributionModalBoundary` del Tablero (C-tablero-frontend-04); acá, review
 * C-agregar-bloque-texto-informes-01.
 *
 * El primer error REMONTA el editor una vez antes de rendirse (QA-texto-M01, 26/10: el aviso salió
 * una vez al abrir otro informe por la navegación interna, con el editor ya descargado, y con una
 * carga completa de la misma página no se repitió). Cada error va a Sentry con su pila: la barrera
 * lo atrapa, así que sin esto nadie se enteraría de qué fue. Si el reintento también falla, el
 * bloque queda en sólo lectura y se avisa que hay que recargar para editarlo.
 */
export class EditorLoadBoundary extends Component {
    state = {failed: false, attempt: 0};

    static getDerivedStateFromError() {
        return {failed: true};
    }

    componentDidCatch(error, info) {
        Sentry.captureException(error, {extra: {
            scope: 'informes_text_editor', attempt: this.state.attempt,
            componentStack: info ? info.componentStack : null,
        }});
        if (this.state.attempt === 0) {
            this.setState({failed: false, attempt: 1});
            return;
        }
        this.props.onError();
    }

    render() {
        if (this.state.failed) return this.props.fallback;
        // La `key` cambia con el reintento: el editor se monta de nuevo, desde cero.
        return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>;
    }
}

/**
 * El cuerpo de un bloque de texto (card 1YGcVgVw). En el editor, el texto se edita en el lugar;
 * en sólo lectura (portal, vista previa) se dibuja con `RichTextView`.
 *
 * No recibe `data`: un texto no pasa por el motor, su contenido es `panel.content` (el layout).
 */
export function TextBody({panel, readOnly = false, onContentChange = null}) {
    const {t} = useTranslation();
    const content = (panel && panel.content) || '';
    if (readOnly || !onContentChange) return <RichTextView html={content}/>;
    const placeholder = t('informes.panel.text.placeholder',
        'Escribe aquí los insights del período, los cambios que hicieron o las recomendaciones para el próximo mes.');
    const readOnlyView = <RichTextView html={content}/>;
    const onLoadError = () => toast.error(
        t('informes.panel.text.load_error', 'No pudimos cargar el editor de texto. Recarga la página para editarlo.'),
        {toastId: LOAD_ERROR_TOAST_ID},
    );
    return (
        <EditorLoadBoundary fallback={readOnlyView} onError={onLoadError}>
            {/* Mientras baja el editor se ve el texto tal cual queda: sin salto ni spinner. */}
            <Suspense fallback={readOnlyView}>
                <RichTextEditor value={content} onChange={onContentChange} placeholder={placeholder}/>
            </Suspense>
        </EditorLoadBoundary>
    );
}
